import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { ChevronRight, Home } from 'lucide-react';
import { getApiUrl } from './utils/api';

const buildCrumbs = (activeMain, companyID, jobID, companyName, jobName) => {
  const enc = (v) => encodeURIComponent(v);

  const allCompanies = { label: 'All Companies',    path: '/all-companies' };
  const company      = companyID ? { label: companyName || '…', path: `/company-profile?companyID=${enc(companyID)}` } : null;
  const viewJobs     = companyID ? { label: 'View Posted Jobs', path: `/all-jobs?companyID=${enc(companyID)}` }        : null;
  const postJob      = companyID ? { label: 'Post Job',         path: `/create-JD?companyID=${enc(companyID)}` }       : null;
  const job          = (companyID && jobID)
    ? { label: jobName || '…', path: `/job-description?companyID=${enc(companyID)}&jobID=${enc(jobID)}` }
    : null;

  const last = (label) => ({ label, path: null });

  switch (activeMain) {
    case 'dashboard':             return [last('Dashboard')];
    case 'super-admin-management':return [last('Admin Management')];
    case 'add-company':           return [allCompanies, last('Add Company Profile')];
    case 'all-companies':         return [last('All Companies')];
    case 'company-profile':       return [allCompanies, company && { ...company, path: null }].filter(Boolean);
    case 'edit-company-profile':  return [allCompanies, company, last('Edit Company Profile')].filter(Boolean);
    case 'view-jobs':             return [allCompanies, company, last('View Posted Jobs')].filter(Boolean);
    case 'create-jd':             return [allCompanies, company, postJob, last('Create JD')].filter(Boolean);
    case 'upload-jd':             return [allCompanies, company, postJob, last('Upload JD')].filter(Boolean);
    case 'generate-jd':           return [allCompanies, company, postJob, last('Generate JD with AI')].filter(Boolean);
    case 'after-jd':              return [allCompanies, company, postJob, last('After JD')].filter(Boolean);
    case 'job-description':       return [allCompanies, company, viewJobs, job && { ...job, path: null }].filter(Boolean);
    case 'enroll-candidate':      return [allCompanies, company, viewJobs, job, last('Enroll Candidate')].filter(Boolean);
    case 'view-scheduled-tests':  return [allCompanies, company, viewJobs, job, last('Scheduled Tests')].filter(Boolean);
    case 'view-all-candidates':   return [allCompanies, company, viewJobs, job, last('All Candidates')].filter(Boolean);
    case 'edit-job':              return [allCompanies, company, viewJobs, job, last('Edit Job')].filter(Boolean);
    case 'view-test':             return [allCompanies, company, viewJobs, job, last('View Test')].filter(Boolean);
    case 'credit-config':         return [{ label: 'Credit Management', path: '/admin/credits/plans' }, last('Configuration')];
    case 'credit-plans':          return [last('Credit Management')];
    case 'credit-companies':      return [{ label: 'Credit Management', path: '/admin/credits/plans' }, last('Company Credits')];
    case 'credit-usage':          return [{ label: 'Credit Management', path: '/admin/credits/plans' }, last('Usage')];
    case 'credit-coupons':        return [{ label: 'Credit Management', path: '/admin/credits/plans' }, last('Coupons')];
    default:                      return [last('Dashboard')];
  }
};

export default function Breadcrumbs({ activeMain, selectedCompany, selectedJob, navigate }) {
  const location  = useLocation();
  const companyID = new URLSearchParams(location.search).get('companyID');
  const jobID     = new URLSearchParams(location.search).get('jobID');

  const [fetchedCompanyName, setFetchedCompanyName] = useState('');
  const [fetchedJobName,     setFetchedJobName]     = useState('');

  // Prefer data already loaded in parent; only fetch when missing
  const companyName = selectedCompany?.company_name || fetchedCompanyName;
  const jobName     = selectedJob?.job_title        || fetchedJobName;

  useEffect(() => {
    if (companyID && !selectedCompany?.company_name) {
      fetch(getApiUrl(`/api/get_company_by_ID?companyID=${encodeURIComponent(companyID)}`), { credentials: 'include' })
        .then(r => r.ok ? r.json() : null)
        .then(d => d && setFetchedCompanyName(d.company_name))
        .catch(() => {});
    }
  }, [companyID, selectedCompany]);

  useEffect(() => {
    if (jobID && !selectedJob?.job_title) {
      fetch(getApiUrl(`/api/job_description?jobId=${jobID}`), { credentials: 'include' })
        .then(r => r.ok ? r.json() : null)
        .then(d => d && setFetchedJobName(d.job_title))
        .catch(() => {});
    }
  }, [jobID, selectedJob]);

  const crumbs = buildCrumbs(activeMain, companyID, jobID, companyName, jobName);

  return (
    <nav className="flex items-center gap-1.5 px-6 py-3 bg-white border-b border-slate-100 text-sm font-[Poppins,sans-serif]">
      {crumbs.map((crumb, index) => {
        const isLast  = index === crumbs.length - 1;
        const isFirst = index === 0;

        return (
          <span key={index} className="flex items-center gap-1.5">
            {crumb.path && !isLast ? (
              <button
                onClick={() => navigate(crumb.path)}
                className="flex items-center gap-1 text-indigo-600 hover:text-indigo-800 hover:underline transition-colors border-none bg-transparent cursor-pointer text-sm p-0"
              >
                {isFirst && <Home className="w-3.5 h-3.5" />}
                {crumb.label}
              </button>
            ) : (
              <span className={`flex items-center gap-1 ${isLast ? 'font-semibold text-slate-700' : 'text-slate-500'}`}>
                {isFirst && <Home className="w-3.5 h-3.5" />}
                {crumb.label}
              </span>
            )}
            {!isLast && <ChevronRight className="w-3.5 h-3.5 text-slate-400" />}
          </span>
        );
      })}
    </nav>
  );
}
